/**
 * VocalCleanupModal — iter-224
 *
 * Spec from the user:
 *   Vocal Cleanup ▼
 *   ✓ Remove Reverb
 *   ✓ Remove Delay
 *   ✓ Remove Chorus
 *   ✓ Remove Background Noise
 *   ✓ Remove Room Echo
 *
 *   Strength: 0% ───●─── 100%
 *
 * Implementation note: ElevenLabs Audio Isolation does ALL of those
 * cleanup steps in a single pass — the checkboxes here are
 * informational so the user knows what the operation removes. The
 * strength slider blends the cleaned signal with the original dry
 * buffer in the browser: 0% = bypass (keep dry), 100% = full clean.
 *
 * Backend call:  POST /api/voice/cleanup  multipart {audio}
 * Response:      audio/mpeg bytes (decode → AudioBuffer → blend).
 */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { padCh } from "./dawHelpers";

const REMOVE_OPTIONS = [
  { key: "reverb",    label: "Remove Reverb",            hint: "Plate / hall tails baked into the take" },
  { key: "delay",     label: "Remove Delay",             hint: "Echoes and slapbacks" },
  { key: "chorus",    label: "Remove Chorus",            hint: "Doubled / detuned voice layers" },
  { key: "noise",     label: "Remove Background Noise",  hint: "Fans, hiss, traffic, computer fans" },
  { key: "roomEcho",  label: "Remove Room Echo",         hint: "Untreated-room flutter & early reflections" },
];

export default function VocalCleanupModal({
  open,
  onClose,
  channelIdx,
  channelName,
  hasAudio,
  // (channelIdx, cleanedBuffer, strength) => Promise<void>
  onApply,
  // () => Blob|null — exposes the channel's dry WAV blob to upload.
  onGetSourceBlob,
  busy = false,
}) {
  // Strength controls dry/wet mix on the browser side.
  const [strength, setStrength] = useState(100);
  // The 5 toggles are informational — ElevenLabs does all of them in
  // one pass. Pre-checked so users see the full cleanup is happening;
  // unchecking is purely cosmetic for now and doesn't change the API
  // call (clarified in the help-text footer).
  const [picked, setPicked] = useState(() => REMOVE_OPTIONS.reduce((acc, o) => ({ ...acc, [o.key]: true }), {}));
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;
  const toggle = (k) => setPicked((p) => ({ ...p, [k]: !p[k] }));
  const anyOn = Object.values(picked).some(Boolean);

  const handleApply = async () => {
    if (submitting) return;
    if (!hasAudio) {
      toast.error("No audio on this channel", { description: "Record or load a vocal first." });
      return;
    }
    if (!anyOn) {
      toast.message("Nothing to clean", { description: "Tick at least one cleanup option." });
      return;
    }
    setSubmitting(true);
    try {
      const blob = onGetSourceBlob?.();
      if (!blob) {
        toast.error("Could not encode channel audio");
        return;
      }
      const form = new FormData();
      form.append("audio", blob, `ch${padCh(channelIdx)}-dry.wav`);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voice/cleanup`, {
        method: "POST",
        body: form,
      });
      if (!res.ok) {
        let detail = "";
        try { detail = (await res.json())?.detail || ""; } catch { detail = await res.text().catch(() => ""); }
        throw new Error(`HTTP ${res.status}: ${String(detail).slice(0, 240)}`);
      }
      const cleanedAb = await res.arrayBuffer();
      // Hand the buffer off to the parent; it decodes + blends + loads
      // into the channel (engine owns the AudioContext + dry buffer).
      // The parent emits its own success toast with an A/B compare
      // action — see ELPMStudioDAW.applyVocalCleanup.
      await onApply?.(channelIdx, cleanedAb, strength);
      onClose?.();
    } catch (e) {
      toast.error("Vocal cleanup failed", { description: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="vocal-cleanup-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] grid place-items-center bg-black/75 p-4 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, y: 10 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 10 }}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md rounded-3xl border border-emerald-400/20 bg-zinc-950/95 p-5 shadow-[0_30px_60px_-20px_rgba(16,185,129,0.45)]"
        >
          <div className="mb-4 flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-2xl bg-gradient-to-br from-emerald-400 to-cyan-400 text-white shadow-[0_0_18px_rgba(16,185,129,0.5)]">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-lg font-semibold text-white">Vocal Cleanup</h2>
              <p className="font-mono text-[10px] uppercase tracking-wider text-white/45">
                CH {padCh(channelIdx)} · {channelName || "—"}
              </p>
            </div>
            <button
              type="button"
              data-testid="vocal-cleanup-close"
              onClick={onClose}
              className="rounded-full bg-white/[0.05] p-1.5 text-white/55 transition hover:bg-white/[0.12] hover:text-white"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <ul className="space-y-1.5">
            {REMOVE_OPTIONS.map((opt) => {
              const on = !!picked[opt.key];
              return (
                <li
                  key={opt.key}
                  data-testid={`vocal-cleanup-opt-${opt.key}`}
                  onClick={() => toggle(opt.key)}
                  className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2 transition ${
                    on
                      ? "border-emerald-400/40 bg-emerald-500/[0.08]"
                      : "border-white/10 bg-white/[0.02] hover:bg-white/[0.06]"
                  }`}
                >
                  <span
                    aria-hidden
                    className={`mt-0.5 grid h-4 w-4 flex-shrink-0 place-items-center rounded ${
                      on ? "bg-emerald-400 text-black" : "border border-white/25"
                    }`}
                  >
                    {on && <span className="font-mono text-[10px] font-bold">✓</span>}
                  </span>
                  <span className="flex-1">
                    <span className={`block font-display text-sm font-semibold ${on ? "text-emerald-100" : "text-white/70"}`}>
                      {opt.label}
                    </span>
                    <span className="block text-[10px] text-white/45">{opt.hint}</span>
                  </span>
                </li>
              );
            })}
          </ul>

          <div className="mt-4 rounded-2xl border border-white/[0.06] bg-black/30 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-wider text-white/55">Strength</span>
              <span
                data-testid="vocal-cleanup-strength-label"
                className="font-mono text-[11px] text-emerald-200"
              >
                {strength}%
              </span>
            </div>
            <input
              data-testid="vocal-cleanup-strength"
              type="range"
              min="0"
              max="100"
              value={strength}
              onChange={(e) => setStrength(Number(e.target.value))}
              className="w-full accent-emerald-400"
            />
            <div className="mt-1 flex justify-between font-mono text-[9px] uppercase tracking-wider text-white/40">
              <span>0% · dry</span>
              <span>100% · fully clean</span>
            </div>
          </div>

          <p className="mt-3 rounded-lg border border-emerald-400/15 bg-emerald-500/[0.05] px-3 py-2 text-[10px] leading-relaxed text-emerald-100/80">
            ElevenLabs Audio Isolation runs all selected cleanup steps in a single pass.
            The cleaned signal is blended with your original at the strength you pick — so
            100% = fully processed, 50% = half-and-half, 0% = bypass.
          </p>

          <div className="mt-4 flex items-center gap-2">
            <Button
              data-testid="vocal-cleanup-cancel"
              variant="ghost"
              onClick={onClose}
              disabled={submitting || busy}
              className="flex-1 rounded-xl bg-white/[0.04] text-white/75 hover:bg-white/[0.10]"
            >
              Cancel
            </Button>
            <Button
              data-testid="vocal-cleanup-apply"
              onClick={handleApply}
              disabled={!hasAudio || submitting || busy || !anyOn}
              className="flex-1 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white shadow-[0_0_18px_rgba(16,185,129,0.4)] hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Cleaning…</>
              ) : (
                <><Wand2 className="mr-2 h-4 w-4" />Clean Vocal</>
              )}
            </Button>
          </div>

          <p className="mt-3 text-center font-mono text-[9px] uppercase tracking-wider text-white/35">
            ElevenLabs Audio Isolation · uses your ElevenLabs credits
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
